import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { reportsApi } from '../../utils/reportsApi';
import { api } from '../../utils/api';
import './ReportCommon.css';

function fmt(n) {
  const v = Number(n);
  if (!v) return '—';
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtNet(n) {
  const v = Number(n);
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const BalanceSheetPage = () => {
  const navigate = useNavigate();

  const [periodId,  setPeriodId]  = useState('');
  const [entityId,  setEntityId]  = useState('');

  const [periods,   setPeriods]   = useState([]);
  const [entities,  setEntities]  = useState([]);
  const [result,    setResult]    = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error,     setError]     = useState(null);

  useEffect(() => {
    Promise.all([api.get('/fiscal/fiscal-years'), api.get('/entities')]).then(([fys, ents]) => {
      const allPeriods = (fys || []).flatMap((fy) =>
        (fy.periods || []).map((p) => ({ ...p, fyCode: fy.code }))
      );
      allPeriods.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
      setPeriods(allPeriods);
      setEntities(ents || []);
    }).catch(() => {});
  }, []);

  const params = { periodId: periodId || undefined, entityId: entityId || undefined };

  const run = async () => {
    if (!periodId) { setError('Select a period.'); return; }
    setLoading(true); setError(null); setResult(null);
    try {
      setResult(await reportsApi.getBalanceSheet(params));
    } catch (e) {
      setError(e.message || 'Failed to run report');
    } finally { setLoading(false); }
  };

  const exportXlsx = async () => {
    setExporting(true);
    try { await reportsApi.exportBalanceSheet(params, 'balance-sheet.xlsx'); }
    catch (e) { setError(e.message || 'Export failed'); }
    finally { setExporting(false); }
  };

  const assetLines     = result?.assets?.lines     ?? [];
  const liabilityLines = result?.liabilities?.lines ?? [];

  return (
    <div className="rpt-page">
      <div className="rpt-header">
        <button className="rpt-back" onClick={() => navigate('/reports')}><i className="fas fa-arrow-left"></i> Reports</button>
        <h1 className="rpt-title">Balance Sheet</h1>
      </div>

      <div className="rpt-filters">
        <label className="rpt-label">
          Period <span className="rpt-req">*</span>
          <select value={periodId} onChange={(e) => setPeriodId(e.target.value)} className="rpt-select">
            <option value="">— select period —</option>
            {periods.map((p) => <option key={p.id} value={p.id}>{p.fyCode} · {p.name}</option>)}
          </select>
        </label>
        <label className="rpt-label">
          Entity
          <select value={entityId} onChange={(e) => setEntityId(e.target.value)} className="rpt-select">
            <option value="">All entities</option>
            {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </label>
        <div className="rpt-filter-actions">
          <button className="rpt-btn rpt-btn--primary" onClick={run} disabled={loading || !periodId}>
            {loading ? <><i className="fas fa-spinner fa-spin"></i> Running…</> : <><i className="fas fa-play"></i> Run</>}
          </button>
          {result && (
            <button className="rpt-btn rpt-btn--excel" onClick={exportXlsx} disabled={exporting}>
              {exporting ? <><i className="fas fa-spinner fa-spin"></i> Exporting…</> : <><i className="fas fa-file-excel"></i> Export Excel</>}
            </button>
          )}
        </div>
      </div>

      {error && <div className="rpt-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}

      {result && (
        <div className="rpt-result">
          <div className="rpt-meta">
            Period: <strong>{result.period?.name}</strong>
            {result.entity && <> &nbsp;·&nbsp; <strong>{result.entity.name}</strong></>}
          </div>

          <div className="rpt-table-wrap">
            <table className="rpt-table">
              <thead>
                <tr>
                  <th className="col-code">Code</th>
                  <th className="col-name">Account</th>
                  <th className="col-amt">Closing Dr (EGP)</th>
                  <th className="col-amt">Closing Cr (EGP)</th>
                  <th className="col-amt">Balance</th>
                </tr>
              </thead>
              <tbody>
                {/* Assets */}
                <tr className="row-section-header">
                  <td colSpan="5">
                    <i className="fas fa-building-columns" style={{ marginRight: 8, color: '#1e40af' }}></i>ASSETS
                  </td>
                </tr>
                {assetLines.length === 0 && (
                  <tr><td colSpan="5" className="rpt-empty">No asset accounts with balance.</td></tr>
                )}
                {assetLines.map((r) => (
                  <tr key={r.accountId}>
                    <td className="col-code">{r.code}</td>
                    <td className="col-name">{r.name}</td>
                    <td className="col-amt">{fmt(r.closingDebit)}</td>
                    <td className="col-amt">{fmt(r.closingCredit)}</td>
                    <td className="col-amt" style={{ color: r.balance >= 0 ? '#166534' : '#9a3412', fontWeight: 600 }}>
                      {fmtNet(r.balance)}
                    </td>
                  </tr>
                ))}
                <tr className="row-section-total">
                  <td colSpan="4" style={{ fontWeight: 700 }}>Total Assets</td>
                  <td className="col-amt" style={{ color: '#1e40af', fontWeight: 700 }}>{fmtNet(result.assets.total)}</td>
                </tr>

                <tr><td colSpan="5" style={{ height: 8 }}></td></tr>

                {/* Liabilities */}
                <tr className="row-section-header">
                  <td colSpan="5">
                    <i className="fas fa-scale-balanced" style={{ marginRight: 8, color: '#9f1239' }}></i>LIABILITIES
                  </td>
                </tr>
                {liabilityLines.length === 0 && (
                  <tr><td colSpan="5" className="rpt-empty">No liability accounts with balance.</td></tr>
                )}
                {liabilityLines.map((r) => (
                  <tr key={r.accountId}>
                    <td className="col-code">{r.code}</td>
                    <td className="col-name">{r.name}</td>
                    <td className="col-amt">{fmt(r.closingDebit)}</td>
                    <td className="col-amt">{fmt(r.closingCredit)}</td>
                    <td className="col-amt" style={{ fontWeight: 600 }}>
                      {fmtNet(Math.abs(r.balance))}
                    </td>
                  </tr>
                ))}
                <tr className="row-section-total">
                  <td colSpan="4" style={{ fontWeight: 700 }}>Total Liabilities</td>
                  <td className="col-amt" style={{ color: '#9f1239', fontWeight: 700 }}>{fmtNet(result.liabilities.total)}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr className="row-net">
                  <td colSpan="4">NET INCOME (period)</td>
                  <td className="col-amt" style={{ color: result.netIncome >= 0 ? '#166534' : '#9a3412' }}>
                    {fmtNet(result.netIncome)}
                  </td>
                </tr>
                <tr className="row-net">
                  <td colSpan="4">TOTAL EQUITY (Assets − Liabilities)</td>
                  <td className="col-amt" style={{ color: result.totalEquity >= 0 ? '#166534' : '#9a3412' }}>
                    {fmtNet(result.totalEquity)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="rpt-summary">
            <div className="rpt-summary-card rpt-summary-card--blue">
              <div className="rpt-summary-card__label">Total Assets</div>
              <div className="rpt-summary-card__value">{fmtNet(result.assets.total)}</div>
            </div>
            <div className="rpt-summary-card rpt-summary-card--red">
              <div className="rpt-summary-card__label">Total Liabilities</div>
              <div className="rpt-summary-card__value">{fmtNet(result.liabilities.total)}</div>
            </div>
            <div className={`rpt-summary-card ${result.netIncome >= 0 ? 'rpt-summary-card--green' : 'rpt-summary-card--red'}`}>
              <div className="rpt-summary-card__label">Net Income</div>
              <div className="rpt-summary-card__value">{fmtNet(result.netIncome)}</div>
            </div>
            <div className={`rpt-summary-card ${result.totalEquity >= 0 ? 'rpt-summary-card--green' : 'rpt-summary-card--red'}`}>
              <div className="rpt-summary-card__label">Total Equity</div>
              <div className="rpt-summary-card__value">{fmtNet(result.totalEquity)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BalanceSheetPage;
